import { lazy, Suspense, useEffect, useState } from 'react';
import { api, ApiError, NetworkError } from '../lib/api.js';
import { cachedFileResponse } from '../lib/offlineFiles.js';
import { ZoomPane } from './ZoomPane.js';

// pdf.js is heavy (~350 kB gzipped) and only needed when a PDF is
// actually opened — load it on demand so it never weighs down the
// initial app bundle.
const PdfView = lazy(() => import('./PdfView.js').then((m) => ({ default: m.PdfView })));

interface Props {
  documentId: string;
  fileName: string;
  mimeType: string | null;
  onClose: () => void;
}

interface Loaded {
  /** Object URL for the image/download; null for non-image previews. */
  url: string;
  /** Raw bytes, used by the PDF renderer. */
  buffer: ArrayBuffer;
}

/**
 * Full-screen in-app viewer for an attached document file.
 *
 * Why not `window.open(url)`? In an iOS standalone PWA there is no tab
 * chrome, so a `_blank` navigation lands on a blank in-app web view with
 * no way back — the user has to force-quit. Instead we fetch the bytes
 * ourselves (the same-origin request carries the Access cookie) and
 * render them inside the app where Close always works.
 *
 * Images go in a custom pinch/zoom pane (iOS PWAs block native page
 * zoom); PDFs are rasterised page-by-page by pdf.js (an `<iframe>` only
 * shows page one on iOS). A Download action is always offered as a
 * reliable fallback for anything that can't be previewed.
 */
export function FileViewer({ documentId, fileName, mimeType, onClose }: Props) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isImage = (mimeType ?? '').startsWith('image/');
  const isPdf = mimeType === 'application/pdf';

  useEffect(() => {
    let revoked = false;
    let url: string | null = null;
    setLoaded(null);
    setError(null);

    (async () => {
      try {
        const res = await fetch(api.documentFileUrl(documentId), {
          credentials: 'include',
          redirect: 'manual',
        });
        if (res.type === 'opaqueredirect') throw new ApiError(0, 'access_redirect');
        if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
        const buffer = await res.arrayBuffer();
        if (revoked) return;
        url = URL.createObjectURL(new Blob([buffer], { type: mimeType ?? 'application/octet-stream' }));
        setLoaded({ url, buffer });
      } catch (err) {
        if (revoked) return;
        // Network gone (or the session can't refresh offline): fall
        // back to the on-device copy — the pinned Vault/key-document
        // set, or the recently-viewed cache. Same viewer, same bytes.
        const cached = await cachedFileResponse(documentId);
        if (cached) {
          const buffer = await cached.arrayBuffer();
          if (revoked) return;
          url = URL.createObjectURL(
            new Blob([buffer], { type: mimeType ?? 'application/octet-stream' }),
          );
          setLoaded({ url, buffer });
          return;
        }
        if (err instanceof ApiError && err.isAuth) {
          setError('Your session expired. Reload the page and try again.');
        } else if (err instanceof NetworkError || err instanceof TypeError) {
          setError(
            navigator.onLine
              ? "Couldn't load the file — check your connection and try again."
              : 'You’re offline and this file isn’t saved on this device. Vault records and key documents can be kept offline from Settings → Offline.',
          );
        } else {
          setError("Couldn't load the file.");
        }
      }
    })();

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [documentId, mimeType]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="file-viewer" role="dialog" aria-modal="true" aria-label={`Viewing ${fileName}`}>
      <header className="file-viewer__bar">
        <button type="button" className="file-viewer__close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <span className="file-viewer__name">{fileName}</span>
        {loaded && (
          <a className="file-viewer__download" href={loaded.url} download={fileName}>
            Download
          </a>
        )}
      </header>

      <div className="file-viewer__body">
        {error ? (
          <p className="file-viewer__message">{error}</p>
        ) : !loaded ? (
          <p className="file-viewer__message">Loading…</p>
        ) : isPdf ? (
          <Suspense fallback={<p className="file-viewer__message">Loading…</p>}>
            <PdfView data={loaded.buffer} />
          </Suspense>
        ) : isImage ? (
          <ZoomPane>
            <img className="file-viewer__image" src={loaded.url} alt={fileName} />
          </ZoomPane>
        ) : (
          <p className="file-viewer__message">
            This file type can’t be previewed. Use Download to open it.
          </p>
        )}
      </div>
    </div>
  );
}
